import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  // Switch,
  TextField,
  Button,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
  CommanTableContain,
  AddButtonContainer,
  StatusSwitchContainer,
  StyledTextField,
  IconStyles,
  StyledAddButton,
  ActionModal,
  ModalContain,
} from "../../style/Admin/CourseCategory";
import DashboardHeader from "../../components/Common/DashboardHeader";
import Switch from "../../components/Switch.jsx";
import { SwitchContainer } from "../../style/Switch/index.jsx";

import {
  fetchCourseCategories,
  addCourseCategory,
  updateCourseCategory,
  deleteCourseCategory,
} from "../../services/courseServices";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const headers = [
  { id: "id", label: "S.No.", sticky: true },
  { id: "name", label: "Course Category" },
  { id: "description", label: "Description" },
  { id: "status", label: "Active Status" },
  { id: "action", label: "Action" },
];

const CourseCategory = () => {
  const [checked, setChecked] = useState(false);
  const [data, setData] = useState([]);
  const [newCategories, setNewCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(null);
  const [editedCourseName, setEditedCourseName] = useState("");
  const [editedCourseDescription, setEditedCourseDescription] = useState("");
  const [editedCourseStatus, setEditedCourseStatus] = useState("Active");
  const [open, setOpen] = useState(false); // State for delete confirmation modal
  const [deleteId, setDeleteId] = useState(null); // ID of the category to delete

  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message);
    } else if (type === "error") {
      toast.error(message);
    } else if (type === "warning") {
      toast.warn(message);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchCourseCategories({});
      if (response.success) {
        const categories = response.categories.map((category) => ({
          id: category.id,
          name: category.name,
          description: category.description,
          status:
            category.status.charAt(0).toUpperCase() + category.status.slice(1),
          isActive: category.status === "active",
        }));
        setData(categories);

        if (categories.length === 0) {
          showToast("Course Categories are not available.", "warning");
        }
      }
    } catch (error) {
      showToast("An error occurred while fetching course categories.", "error");
    }
  };

  const handleAddNewRow = () => {
    setNewCategories([
      ...newCategories,
      { id: Date.now(), name: "", description: "", status: "Active" },
    ]);
  };

  const handleNewCategoryChange = (id, field, value) => {
    setNewCategories((prev) =>
      prev.map((category) =>
        category.id === id ? { ...category, [field]: value } : category
      )
    );
  };

  const handleSaveNewCategory = async (id) => {
    const newCategory = newCategories.find((category) => category.id === id);

    if (!newCategory.name.trim()) {
      showToast("Course Category name cannot be empty!", "warning");
      return;
    }

    try {
      const response = await addCourseCategory({
        name: newCategory.name,
        description: newCategory.description,
        status: newCategory.status.toLowerCase(),
      });
      if (response.success) {
        showToast("Course Category added successfully!");
        fetchCategories(); // Refresh categories after adding
        setNewCategories((prev) =>
          prev.filter((category) => category.id !== id)
        );
      } else {
        showToast("Failed to add Course Category. Please try again.", "error");
      }
    } catch (error) {
      showToast(
        "An error occurred while adding the Course Category. Please try again.",
        "error"
      );
    }
  };

  const handleCancelNewCategory = (id) => {
    setNewCategories((prev) => prev.filter((category) => category.id !== id));
  };

  const handleUpdateCourse = async (id) => {
    if (!id) {
      showToast("Error: Course Category ID is undefined.", "error");
      return;
    }

    const updatedCourse = {
      name: editedCourseName,
      description: editedCourseDescription,
      status: editedCourseStatus.toLowerCase(),
    };

    try {
      const response = await updateCourseCategory(id, updatedCourse);
      if (response.success) {
        showToast("Course Category updated successfully!");
        fetchCategories(); // Refresh categories after updating
        setIsEditing(null);
      } else {
        showToast(
          "Failed to update Course Category. Please try again.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        "An error occurred while updating the Course Category. Please try again.",
        "error"
      );
    }
  };

  // const handleDeleteCourse = (id) => {
  //   setDeleteId(id); // Set the ID of the category to delete
  //   setOpen(true); // Open the delete confirmation modal
  // };

  const handleConfirm = async () => {
    if (!deleteId) {
      showToast("Error: Course Category ID is undefined.", "error");
      return;
    }

    try {
      const response = await deleteCourseCategory(deleteId);
      if (response.success) {
        showToast("Course Category deleted successfully!");
        fetchCategories();
      } else {
        showToast(
          "Failed to delete Course Category. Please try again.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        "An error occurred while deleting the Course Category. Please try again.",
        "error"
      );
    } finally {
      setOpen(false); // Close the modal
      setDeleteId(null); // Reset the delete ID
    }
  };

  const handleCancel = () => {
    setOpen(false); // Close the modal
    setDeleteId(null); // Reset the delete ID
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <>
      <AddButtonContainer>
        <DashboardHeader title="Course Category" />
        <StyledAddButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNewRow}
        >
          Add Course Category
        </StyledAddButton>
      </AddButtonContainer>

      <CommanTableContain>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header.id}>{header.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {newCategories.map((category, index) => (
              <TableRow key={category.id}>
                <TableCell>{data.length + index + 1}</TableCell>
                <TableCell>
                  <StyledTextField
                    value={category.name}
                    onChange={(e) =>
                      handleNewCategoryChange(category.id, "name", e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <StyledTextField
                    value={category.description}
                    onChange={(e) =>
                      handleNewCategoryChange(
                        category.id,
                        "description",
                        e.target.value
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <StatusSwitchContainer>
                    <Switch
                      checked={category.status === "Active"}
                      onChange={() =>
                        handleNewCategoryChange(
                          category.id,
                          "status",
                          category.status === "Active" ? "Inactive" : "Active"
                        )
                      }
                    />
                  </StatusSwitchContainer>
                </TableCell>
                <TableCell>
                  <IconStyles>
                    <DoneIcon
                      className="icon-done"
                      onClick={() => handleSaveNewCategory(category.id)}
                    />
                    <CloseIcon
                      className="icon-close"
                      onClick={() => handleCancelNewCategory(category.id)}
                    />
                  </IconStyles>
                </TableCell>
              </TableRow>
            ))}
            {data.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  {isEditing === item.id ? (
                    <StyledTextField
                      value={editedCourseName}
                      onChange={(e) => setEditedCourseName(e.target.value)}
                    />
                  ) : (
                    item.name
                  )}
                </TableCell>
                <TableCell>
                  {isEditing === item.id ? (
                    <TextField
                      className="styled_text_field"
                      value={editedCourseDescription}
                      onChange={(e) =>
                        setEditedCourseDescription(e.target.value)
                      }
                    />
                  ) : (
                    item.description
                  )}
                </TableCell>
                <TableCell>
                  {isEditing === item.id ? (
                    <StatusSwitchContainer>
                      <Switch
                        checked={editedCourseStatus === "Active"}
                        onChange={() =>
                          setEditedCourseStatus(
                            editedCourseStatus === "Active"
                              ? "Inactive"
                              : "Active"
                          )
                        }
                      />
                    </StatusSwitchContainer>
                  ) : (
                    <SwitchContainer>
                      <Switch checked={item.isActive} disabled />
                    </SwitchContainer>
                  )}
                </TableCell>
                <TableCell>
                  {isEditing === item.id ? (
                    <IconStyles>
                      <DoneIcon
                        className="icon-done"
                        onClick={() => handleUpdateCourse(item.id)}
                      />
                      <CloseIcon
                        className="icon-close"
                        onClick={() => setIsEditing(null)}
                      />
                    </IconStyles>
                  ) : (
                    <IconStyles>
                      <EditNoteIcon
                        className="icon-edit"
                        onClick={() => {
                          setIsEditing(item.id);
                          setEditedCourseName(item.name);
                          setEditedCourseDescription(item.description);
                          setEditedCourseStatus(item.status);
                        }}
                      />
                    </IconStyles>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CommanTableContain>

      {open && (
        <ModalContain>
          <ActionModal>
            <div className="IconContain">
              <DeleteForeverIcon />
            </div>
            <h4 className="Title">Are you sure you want to delete this?</h4>
            <p>This action can't be undone!</p>
            <div className="ButtonContain">
              <Button
                variant="contained"
                className="Delete"
                onClick={handleConfirm}
              >
                Delete
              </Button>
              <Button
                variant="contained"
                className="Cancel"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </ActionModal>
        </ModalContain>
      )}

      <ToastContainer autoClose={3000} />
    </>
  );
};

export default CourseCategory;
